import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  inventoryService,
  warehousesService,
  type RecordInwardPayload,
  type InwardLine,
} from '@/services/inventory';
import { purchaseOrdersService } from '@/services/purchase-orders';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { InventoryItem, ItemUnit } from '@/types/inventory';
import { pickerLabel } from '@/lib/inventory-label';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill with this item if opened from a per-item context. */
  prefillItem?: InventoryItem | null;
  /**
   * Slice 16 — when set, the form pre-populates lines with the PO's
   * outstanding qty (ordered − received), defaults the warehouse to
   * the PO's destination, and stamps `purchaseOrderId` on submit so
   * the BE bumps the PO's `receivedQuantity` per line.
   */
  prefillPurchaseOrderId?: string;
}

interface DraftLine {
  key: number;
  /** Picked item id — can be a parent-with-variants OR a standalone item. */
  itemId: string;
  warehouseId: string;
  /**
   * Standalone qty. Used only when the picked item has no variants
   * (`item.hasVariants === false`). Expressed in the line's selected
   * UoM; converted to base on submit.
   */
  quantity: string;
  /** Selected alternate UoM id; null = base unit. Standalone only. */
  unitId: string | null;
  /**
   * Per-variant qty grid. Used only when the picked item is a
   * parent-with-variants (`item.hasVariants === true`). Keys are
   * variant InventoryItem ids; values are decimal-string qtys in base
   * unit (parents-with-variants don't expose a UoM picker — apparel
   * doesn't typically need per-variant UoM and the simpler grid wins
   * the UX). Empty / zero entries are dropped on submit.
   */
  variantQuantities: Record<string, string>;
  reason: string;
}

let LINE_KEY = 1;
const newLine = (itemId = '', warehouseId = ''): DraftLine => ({
  key: LINE_KEY++,
  itemId,
  warehouseId,
  quantity: '',
  unitId: null,
  variantQuantities: {},
  reason: '',
});

/**
 * Bulk inward (receipt) — one or more lines of (item, warehouse, qty).
 * Used when a supplier delivery arrives. Each line writes a `INBOUND`
 * stock movement; the audit trail captures the optional reason / reference.
 *
 * Multi-UoM (Bucket C, Slice 2): the operator can pick any UoM defined on
 * the item. Quantities are entered in the selected UoM but always
 * converted to base before being sent to the API — the BE only ever
 * stores base units.
 */
export function InventoryInwardSheet({
  open,
  onOpenChange,
  prefillItem,
  prefillPurchaseOrderId,
}: Props) {
  const qc = useQueryClient();
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');

  // PO pre-fill: pull the PO + its outstanding lines.
  const poQuery = useQuery({
    queryKey: ['purchase-orders', 'detail', prefillPurchaseOrderId],
    queryFn: () => purchaseOrdersService.get(prefillPurchaseOrderId!),
    enabled: !!prefillPurchaseOrderId && open,
  });
  const prefillPo = poQuery.data;

  // Receive Stock — picker shows ROOTS (parents + standalone items). For
  // parents WITH variants, the LineRow expands into a per-variant qty
  // grid below the picker. For standalone items, the picker behaves like
  // before (single qty input). This is the "smart UI per item definition"
  // pattern operators expect for variant-heavy catalogs (apparel: size
  // × colour × … grids).
  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { status: 'ACTIVE', forSelect: 'inward' }],
    queryFn: () => inventoryService.list({ status: 'ACTIVE', pageSize: 200 }),
    enabled: open,
    staleTime: 30_000,
  });

  const warehousesQuery = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => warehousesService.list(),
    enabled: open,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!open) return;
    if (prefillPo) {
      // Hydrate lines from the PO's outstanding receipts. Skip lines
      // already fully received.
      const drafts: DraftLine[] = prefillPo.items
        .map((it) => {
          const outstanding =
            Number(it.quantity) - Number(it.receivedQuantity);
          if (outstanding <= 0) return null;
          const line = newLine(it.itemId, prefillPo.warehouseId);
          line.quantity = String(outstanding);
          return line;
        })
        .filter((l): l is DraftLine => !!l);
      setLines(drafts.length > 0 ? drafts : [newLine('', prefillPo.warehouseId)]);
      setReference(prefillPo.poNumber);
      setReason(`Receipt against PO ${prefillPo.poNumber}`);
    } else {
      setLines([newLine(prefillItem?.id ?? '', '')]);
      setReference('');
      setReason('');
    }
  }, [open, prefillItem, prefillPo]);

  const items = itemsQuery.data?.items ?? [];
  const warehouses = warehousesQuery.data ?? [];
  const itemById = useMemo(() => {
    const m = new Map<string, InventoryItem>();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  // Compute base-unit quantities for submission. Each line: if a UoM is
  // selected, multiply displayQty by its conversionFactor; else use as-is.
  const computeBaseQty = (line: DraftLine, units: ItemUnit[] | undefined): number => {
    const raw = Number(line.quantity);
    if (!Number.isFinite(raw) || raw <= 0) return 0;
    if (!line.unitId) return raw;
    const u = units?.find((x) => x.id === line.unitId);
    if (!u) return raw;
    return raw * Number(u.conversionFactor);
  };

  const mutation = useMutation({
    mutationFn: (payload: RecordInwardPayload) => inventoryService.recordInward(payload),
    onSuccess: (res) => {
      toast.success(`Recorded inward · ${res.lineCount} ${res.lineCount === 1 ? 'line' : 'lines'}`);
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onOpenChange(false);
    },
  });

  const addLine = () => setLines((ls) => [...ls, newLine()]);
  const removeLine = (key: number) =>
    setLines((ls) => (ls.length === 1 ? ls : ls.filter((l) => l.key !== key)));

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const built: InwardLine[] = [];
    for (const line of lines) {
      if (!line.itemId || !line.warehouseId) continue;
      const item = itemById.get(line.itemId);
      if (!item) continue;

      // PARENT — emit one BE line per non-zero variant. Variant qtys
      // are entered in base unit (no UoM picker on the variant grid).
      if (item.hasVariants) {
        for (const [variantId, qtyStr] of Object.entries(line.variantQuantities)) {
          const qty = Number(qtyStr);
          if (!Number.isFinite(qty) || qty <= 0) continue;
          built.push({
            itemId: variantId,
            warehouseId: line.warehouseId,
            quantity: qty,
            reason: line.reason.trim() || undefined,
          });
        }
        continue;
      }

      // STANDALONE — single qty + UoM conversion. Pull each line's
      // alternate-UoM list out of the React Query cache (LineRow
      // populated it); fall back to a fresh fetch if missing.
      if (!Number(line.quantity)) continue;
      let units = qc.getQueryData<ItemUnit[]>(['inventory', 'units', line.itemId]);
      if (units === undefined) {
        units = await qc.fetchQuery({
          queryKey: ['inventory', 'units', line.itemId],
          queryFn: () => inventoryService.listUnits(line.itemId),
          staleTime: 30_000,
        });
      }
      const baseQty = computeBaseQty(line, units);
      if (baseQty <= 0) continue;
      built.push({
        itemId: line.itemId,
        warehouseId: line.warehouseId,
        quantity: baseQty,
        reason: line.reason.trim() || undefined,
      });
    }
    if (built.length === 0) return;

    mutation.mutate({
      lines: built,
      // When pre-filled from a PO, the BE auto-resolves per-line PO line
      // links via the top-level purchaseOrderId so receivedQuantity bumps
      // and the PO status auto-advances atomically with the inward write.
      ...(prefillPurchaseOrderId
        ? { purchaseOrderId: prefillPurchaseOrderId }
        : {
            referenceType: reference ? 'po' : undefined,
            referenceId: reference || undefined,
          }),
      reason: reason.trim() || undefined,
    });
  };

  /**
   * "Valid line" semantics:
   *   • STANDALONE — item picked + warehouse picked + qty > 0
   *   • PARENT     — item picked + warehouse picked + at least one
   *                  variant has qty > 0
   * Mirrors the submit-time filter so the footer count + button label
   * line up with what will actually be sent to the BE.
   */
  const validCount = lines.filter((l) => {
    if (!l.itemId || !l.warehouseId) return false;
    const item = itemById.get(l.itemId);
    if (!item) return false;
    if (item.hasVariants) {
      return Object.values(l.variantQuantities).some((q) => Number(q) > 0);
    }
    return Number(l.quantity) > 0;
  }).length;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Receive stock"
      description="Record a supplier delivery. Pick a SKU below — variant-tracked items expand into a per-variant grid so you can enter sizes/colours in one go."
      submitLabel={`Receive ${validCount || 0} item${validCount === 1 ? '' : 's'}`}
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="4xl"
      fixedHeight="lg"
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField
          label="Reference (PO #, GRN #)"
          htmlFor="reference"
          hint="Optional — links the receipt to a purchase order in the audit trail."
        >
          <Input
            id="reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="PO-2026-0042"
            className="font-mono"
          />
        </FormField>
        <FormField
          label="Notes"
          htmlFor="reason"
          hint="Optional — captured in the audit trail."
        >
          <Textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Vendor delivery · QC pending"
            rows={1}
            className="resize-none"
          />
        </FormField>
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Items being received
          </div>
          <div className="text-[11px] text-muted-foreground">
            {validCount} of {lines.length} ready
          </div>
        </div>
        <div className="space-y-4">
          {lines.map((l) => (
            <LineRow
              key={l.key}
              line={l}
              item={l.itemId ? itemById.get(l.itemId) ?? null : null}
              items={items}
              warehouses={warehouses}
              itemsLoading={itemsQuery.isLoading}
              warehousesLoading={warehousesQuery.isLoading}
              canRemove={lines.length > 1}
              onChange={(next) =>
                setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, ...next } : x)))
              }
              onRemove={() => removeLine(l.key)}
            />
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addLine}>
          <Plus className="h-3.5 w-3.5" />
          Add another item
        </Button>
      </div>
    </FormSheet>
  );
}

interface LineRowProps {
  line: DraftLine;
  item: InventoryItem | null;
  items: InventoryItem[];
  warehouses: Array<{ id: string; code: string; name: string }>;
  itemsLoading: boolean;
  warehousesLoading: boolean;
  canRemove: boolean;
  onChange: (next: Partial<DraftLine>) => void;
  onRemove: () => void;
}

function LineRow({
  line,
  item,
  items,
  warehouses,
  itemsLoading,
  warehousesLoading,
  canRemove,
  onChange,
  onRemove,
}: LineRowProps) {
  // Treat ANY item with declared variants OR a non-zero variantCount
  // as variant-tracked. Catches the data-drift case where a parent
  // has child variants in DB but its hasVariants flag wasn't flipped.
  const isParentWithVariants =
    !!item && (item.hasVariants || (item.variantCount ?? 0) > 0);

  // Per-line UoM list (standalone only — variants share parent UoM
  // and the variant grid persists quantities in base unit directly).
  const unitsQuery = useQuery({
    queryKey: ['inventory', 'units', line.itemId],
    queryFn: () => inventoryService.listUnits(line.itemId),
    enabled: !!line.itemId && !isParentWithVariants,
    staleTime: 30_000,
  });
  // Variants for this parent — only fetched when the picked item has
  // variants. Each variant row gets its own qty input in the grid.
  const variantsQuery = useQuery({
    queryKey: ['inventory', 'variants', line.itemId],
    queryFn: () => inventoryService.listVariants(line.itemId),
    enabled: !!line.itemId && isParentWithVariants,
    staleTime: 30_000,
  });
  const variants = variantsQuery.data ?? [];

  const units = unitsQuery.data ?? [];
  const baseUnit = item?.unitOfMeasure ?? '';

  // When a standalone SKU is chosen, default the line to its purchase-
  // default UoM (if any). Effect deliberately watches itemId so re-picking
  // a SKU resets. Skipped for parents-with-variants (no UoM picker).
  useEffect(() => {
    if (!line.itemId || isParentWithVariants || !units.length) return;
    if (line.unitId !== null) return;
    const def = units.find((u) => u.isPurchaseDefault);
    if (def) onChange({ unitId: def.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.itemId, units.length, isParentWithVariants]);

  const selectedUnit = line.unitId ? units.find((u) => u.id === line.unitId) ?? null : null;
  const rawQty = Number(line.quantity);
  const baseQty =
    Number.isFinite(rawQty) && rawQty > 0
      ? selectedUnit
        ? rawQty * Number(selectedUnit.conversionFactor)
        : rawQty
      : 0;

  const onPickItem = (newItemId: string) => {
    // Reset both standalone + variant state when re-picking the item.
    onChange({
      itemId: newItemId,
      unitId: null,
      quantity: '',
      variantQuantities: {},
    });
  };

  const setVariantQty = (variantId: string, qty: string) => {
    onChange({
      variantQuantities: { ...line.variantQuantities, [variantId]: qty },
    });
  };

  // Sum of typed variant qtys — surfaces in the line footer so the
  // operator knows total units about to land.
  const variantGridTotal = Object.values(line.variantQuantities).reduce(
    (acc, v) => acc + (Number(v) || 0),
    0,
  );

  const itemPicked = !!item;
  const filledVariantCount = Object.values(line.variantQuantities).filter(
    (q) => Number(q) > 0,
  ).length;

  return (
    <div className="overflow-hidden rounded-lg border border-border/70 bg-card shadow-sm">
      {/* ── Header: item picker + warehouse + remove ────────────────── */}
      <div className="grid grid-cols-12 items-start gap-3 border-b border-border/40 bg-muted/20 p-3">
        <div className="col-span-6 space-y-1">
          <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Item
          </label>
          <Select value={line.itemId} onValueChange={onPickItem}>
            <SelectTrigger className="h-9 text-[13px]">
              <SelectValue placeholder={itemsLoading ? 'Loading SKUs…' : 'Pick a SKU…'} />
            </SelectTrigger>
            <SelectContent>
              {items.map((it) => (
                <SelectItem key={it.id} value={it.id}>
                  <span className="font-mono text-[11px] text-muted-foreground">{it.sku}</span>
                  <span className="ml-2">{pickerLabel(it)}</span>
                  {(it.hasVariants || (it.variantCount ?? 0) > 0) && (
                    <span className="ml-2 text-[10.5px] text-muted-foreground">
                      · {it.variantCount ?? 0} variant{it.variantCount === 1 ? '' : 's'}
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {itemPicked && (
            <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
              <span className="text-[12.5px] font-semibold text-foreground">
                {item.name}
              </span>
              <span className="font-mono text-[10.5px] text-muted-foreground">
                {item.sku}
              </span>
              {isParentWithVariants ? (
                <span className="rounded-full bg-info/15 px-1.5 py-0.5 text-[10px] font-semibold text-info">
                  Variant-tracked
                </span>
              ) : (
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  Standalone
                </span>
              )}
            </div>
          )}
        </div>
        <div className="col-span-5 space-y-1">
          <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Warehouse
          </label>
          <Select
            value={line.warehouseId}
            onValueChange={(v) => onChange({ warehouseId: v })}
          >
            <SelectTrigger className="h-9 text-[13px]">
              <SelectValue placeholder={warehousesLoading ? 'Loading…' : 'Pick a warehouse…'} />
            </SelectTrigger>
            <SelectContent>
              {warehouses.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  <span className="font-mono text-[11px] text-muted-foreground">{w.code}</span>
                  <span className="ml-2">{w.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-1 flex items-start justify-end pt-5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            disabled={!canRemove}
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            title="Remove this item from the receipt"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* ── Body: variant grid OR standalone qty ─────────────────────── */}
      <div className="p-3">
        {!itemPicked && (
          <div className="rounded-md border border-dashed border-border/60 bg-muted/10 px-4 py-6 text-center text-[12.5px] text-muted-foreground">
            Pick an item above to see its receive form.
          </div>
        )}

        {/* Parent-with-variants — per-variant qty grid. */}
        {itemPicked && isParentWithVariants && (
          <>
            {variantsQuery.isLoading ? (
              <div className="rounded-md border border-dashed border-border/60 bg-muted/10 px-4 py-6 text-center text-[12.5px] text-muted-foreground">
                Loading variants for {item.name}…
              </div>
            ) : variantsQuery.error ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/[0.04] px-4 py-3 text-[12.5px] text-destructive">
                Could not load variants:{' '}
                {variantsQuery.error instanceof Error
                  ? variantsQuery.error.message
                  : 'Unknown error'}
              </div>
            ) : variants.length === 0 ? (
              <div className="rounded-md border border-warning/30 bg-warning/[0.05] px-4 py-4 text-[12.5px] text-[hsl(38_85%_30%)]">
                <div className="font-semibold">No variants defined yet.</div>
                <div className="mt-1 text-[11.5px]">
                  This item is marked variant-tracked but no size/colour combos
                  are configured. Open <em>Edit item → Variants</em> to set
                  them up first, then come back here to receive stock.
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-md border border-border/60">
                <table className="w-full table-fixed text-[12.5px]">
                  <thead className="bg-muted/30 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="w-[20%] px-3 py-2 text-left font-semibold">SKU</th>
                      <th className="w-[40%] px-3 py-2 text-left font-semibold">Variant</th>
                      <th className="w-[18%] px-3 py-2 text-right font-semibold">On hand</th>
                      <th className="w-[22%] px-3 py-2 text-right font-semibold">
                        Receive qty
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40 bg-card">
                    {variants.map((v) => {
                      const attrs = v.variantAttributes
                        ? Object.entries(v.variantAttributes).filter(
                            ([, val]) => val,
                          )
                        : [];
                      const qty = line.variantQuantities[v.id] ?? '';
                      const onHand = v.totalOnHand ?? 0;
                      return (
                        <tr key={v.id}>
                          <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                            {v.sku}
                          </td>
                          <td className="px-3 py-2">
                            {attrs.length > 0 ? (
                              <div className="flex flex-wrap items-center gap-1.5">
                                {attrs.map(([k, val]) => (
                                  <span
                                    key={k}
                                    className="rounded bg-muted/60 px-1.5 py-0.5 text-[10.5px] text-foreground"
                                  >
                                    {k}: {String(val)}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td
                            className={cn(
                              'px-3 py-2 text-right font-mono tabular-nums',
                              onHand === 0 ? 'text-muted-foreground' : 'text-foreground',
                            )}
                          >
                            {fmt.number(onHand)}
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              inputMode="decimal"
                              min={0}
                              step="0.001"
                              value={qty}
                              onChange={(e) => setVariantQty(v.id, e.target.value)}
                              placeholder="0"
                              className="h-8 text-right font-mono tabular-nums"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {variantGridTotal > 0 && (
                    <tfoot className="bg-muted/20 text-[11.5px] font-semibold">
                      <tr>
                        <td colSpan={2} className="px-3 py-2 text-muted-foreground">
                          Total
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                          {filledVariantCount}{' '}
                          variant{filledVariantCount === 1 ? '' : 's'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-foreground">
                          {fmt.number(variantGridTotal)} {baseUnit || 'units'}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </>
        )}

        {/* Standalone — single qty + UoM. */}
        {itemPicked && !isParentWithVariants && (
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-4 space-y-1">
              <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                Quantity
              </label>
              <Input
                type="number"
                inputMode="decimal"
                min={0}
                step="0.001"
                value={line.quantity}
                onChange={(e) => onChange({ quantity: e.target.value })}
                placeholder="0"
                className="h-9 text-right font-mono tabular-nums"
              />
            </div>
            <div className="col-span-4 space-y-1">
              <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                Unit of measure
              </label>
              <Select
                value={line.unitId ?? '__base'}
                onValueChange={(v) => onChange({ unitId: v === '__base' ? null : v })}
              >
                <SelectTrigger className="h-9 font-mono text-[12px]">
                  <SelectValue placeholder="UoM" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__base">
                    <span className="font-mono">{baseUnit || '—'}</span>
                    <span className="ml-2 text-muted-foreground">base</span>
                  </SelectItem>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      <span className="font-mono">{u.code}</span>
                      <span className="ml-2 text-muted-foreground">
                        ×{fmt.number(u.conversionFactor)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedUnit && baseQty > 0 && baseUnit && (
              <div className="col-span-4 flex items-end pb-2 text-[11.5px] text-muted-foreground">
                ↳ Stock will increase by{' '}
                <span className="ml-1 font-mono tabular-nums text-foreground">
                  {fmt.number(baseQty)} {baseUnit}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
